import { createFileRoute } from '@tanstack/react-router'
import { Badge } from '@/common/components/badge'
import { Button } from '@/common/components/button'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Stack } from '@/common/components/stack'
import { Table } from '@/common/components/table'
import { Text } from '@/common/components/text'
import {
  isInFlight,
  useCronTasks,
  useTriggerCronTask,
  type CronRun,
  type CronRunStatus,
  type CronTask,
} from '@/common/hooks/useCronAdmin'

const STATUS_LABELS: Record<CronRunStatus, string> = {
  requested: 'Queued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  skipped: 'Skipped',
}

const STATUS_COLORS: Record<CronRunStatus, 'success' | 'caution' | 'error' | 'info' | 'neutral'> = {
  requested: 'info',
  running: 'info',
  succeeded: 'success',
  failed: 'error',
  skipped: 'neutral',
}

/**
 * API timestamps are naive UTC. Without a designator `new Date` would read them
 * as local time and report the last run at the wrong hour.
 */
function parseUtc(iso: string): Date {
  return new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`)
}

function formatWhen(iso: string): string {
  return parseUtc(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatDuration(seconds: number): string {
  if (seconds < 10) return `${seconds.toFixed(1)}s`
  if (seconds < 60) return `${Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${Math.round(seconds % 60)}s`
}

/** The task's own counts, e.g. `inserted 3 · updated 66`. */
function formatSummary(summary: Record<string, unknown>): string {
  return Object.entries(summary)
    .map(([key, value]) => `${key.replace(/_/g, ' ')} ${String(value)}`)
    .join(' · ')
}

function LastRunCell({ run }: { run: CronRun }) {
  // A run that has not finished is best described by when it was asked for.
  const when = run.finished_at ?? run.started_at ?? run.requested_at
  const who = run.trigger === 'manual' ? (run.requested_by_name ?? 'an admin') : null

  return (
    <Stack gap="4">
      <Text size="sm">{formatWhen(when)}</Text>
      <Text size="xs" color="muted">
        {who ? `Run by ${who}` : 'Scheduled'}
      </Text>
    </Stack>
  )
}

function ResultCell({ task }: { task: CronTask }) {
  if (!task.enabled) {
    return <Text size="sm" color="muted">{task.disabled_reason}</Text>
  }

  const run = task.last_run
  if (!run) return <Text size="sm" color="muted">—</Text>

  return (
    <Stack gap="4">
      {run.summary && Object.keys(run.summary).length > 0 && (
        <Text size="sm">{formatSummary(run.summary)}</Text>
      )}
      {run.error_message && (
        <Text size="sm" color={run.status === 'failed' ? 'error' : 'muted'}>
          {run.error_message}
        </Text>
      )}
    </Stack>
  )
}

function TaskRow({ task }: { task: CronTask }) {
  const trigger = useTriggerCronTask()
  const run = task.last_run
  const running = isInFlight(run)

  return (
    <Table.Row>
      <Table.Cell>
        <Stack gap="4">
          <Text size="sm" weight="medium">
            {task.label}
          </Text>
          <Text size="xs" color="muted">
            {task.description}
          </Text>
        </Stack>
      </Table.Cell>
      <Table.Cell>{run ? <LastRunCell run={run} /> : <Text size="sm" color="muted">Never</Text>}</Table.Cell>
      <Table.Cell>
        {!task.enabled ? (
          <Badge color="neutral" size="sm">
            Disabled
          </Badge>
        ) : run ? (
          <Badge color={STATUS_COLORS[run.status]} size="sm">
            {STATUS_LABELS[run.status]}
          </Badge>
        ) : (
          <Text size="sm" color="muted">
            —
          </Text>
        )}
      </Table.Cell>
      <Table.Cell align="right">
        <Text size="sm">{run?.duration_seconds != null ? formatDuration(run.duration_seconds) : '—'}</Text>
      </Table.Cell>
      <Table.Cell>
        <ResultCell task={task} />
      </Table.Cell>
      <Table.Cell align="right">
        {task.enabled && (
          <Stack gap="4" align="end">
            <Button
              variant="outline"
              size="sm"
              disabled={trigger.isPending || running}
              onClick={() => trigger.mutate(task.name)}
            >
              {running ? 'Running…' : 'Run now'}
            </Button>
            {trigger.isError && (
              <Text size="xs" color="error">
                {(trigger.error as Error).message}
              </Text>
            )}
          </Stack>
        )}
      </Table.Cell>
    </Table.Row>
  )
}

function AdminCron() {
  const { data: tasks, isLoading, error } = useCronTasks()

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <Stack gap="32">
        <Stack gap="8">
          <Text size="xl" weight="bold">
            Cron jobs
          </Text>
          <Text size="sm" color="muted">
            The scheduled tasks that keep employee, holiday and notification data current. A run
            triggered here is queued for the cron service and starts within about a minute.
          </Text>
        </Stack>

        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <Text color="error">Failed to load cron jobs: {error.message}</Text>
        ) : (
          <Table.Root size="md">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Task</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Last run</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell align="right">Duration</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Result</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {(tasks ?? []).map((task) => (
                <TaskRow key={task.name} task={task} />
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Stack>
    </div>
  )
}

export const Route = createFileRoute('/org/cron')({
  component: AdminCron,
})
